import { HolderOutlined } from '@ant-design/icons';
import {
    closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Button, Empty, Flex, Modal, Spin, Tag, theme, Typography,
} from 'antd';
import {
    ReactNode, useEffect, useMemo, useState, 
} from 'react';

export type ReorderModalItem = {
    id: string;
    title: string;
    subtitle?: string | null;
    extra?: ReactNode;
};

type ReorderModalProps = {
    open: boolean;
    title: string;
    items: ReorderModalItem[];
    loading?: boolean;
    saving?: boolean;
    onCancel: () => void;
    onSave: (ids: string[]) => void;
    emptyText?: string;
};

type SortableItemProps = {
    item: ReorderModalItem;
    index: number;
};

const SortableItem = ({ item, index }: SortableItemProps) => {
    const { token } = theme.useToken();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.7 : 1,
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                padding: token.paddingSM,
                boxShadow: isDragging ? token.boxShadowSecondary : 'none',
            }}
        >
            <Flex align="center" gap="middle" justify="space-between">
                <Flex align="center" gap="middle" style={{ minWidth: 0, flex: 1 }}>
                    <Button
                        type="text"
                        icon={<HolderOutlined />}
                        {...attributes}
                        {...listeners}
                        style={{ cursor: 'grab' }}
                    />
                    <Typography.Text type="secondary">
                        {index + 1}
                    </Typography.Text>
                    <Flex vertical style={{ minWidth: 0 }}>
                        <Typography.Text strong ellipsis>
                            {item.title}
                        </Typography.Text>
                        {item.subtitle && (
                            <Typography.Text type="secondary" ellipsis>
                                {item.subtitle}
                            </Typography.Text>
                        )}
                    </Flex>
                </Flex>
                {item.extra}
            </Flex>
        </div>
    );
};

export const ReorderModal = ({
    open,
    title,
    items,
    loading = false,
    saving = false,
    onCancel,
    onSave,
    emptyText = 'Немає записів для сортування',
}: ReorderModalProps) => {
    const [orderedItems, setOrderedItems] = useState<ReorderModalItem[]>([]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 6,
            },
        }),
    );

    useEffect(() => {
        if (open) {
            setOrderedItems(items);
        }
    }, [items, open]);

    const orderedIds = useMemo(() => orderedItems.map((item) => item.id), [orderedItems]);
    const isDirty = useMemo(() => {
        if (items.length !== orderedItems.length) {
            return true;
        }

        return items.some((item, index) => item.id !== orderedItems[index]?.id);
    }, [items, orderedItems]);

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) {
            return;
        }

        setOrderedItems((currentItems) => {
            const oldIndex = currentItems.findIndex((item) => item.id === active.id);
            const newIndex = currentItems.findIndex((item) => item.id === over.id);

            if (oldIndex === -1 || newIndex === -1) {
                return currentItems;
            }

            return arrayMove(currentItems, oldIndex, newIndex);
        });
    };

    return (
        <Modal
            open={open}
            title={title}
            onCancel={onCancel}
            width={720}
            footer={[
                <Button key="cancel" onClick={onCancel} disabled={saving}>
                    Скасувати
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    onClick={() => onSave(orderedIds)}
                    loading={saving}
                    disabled={loading || !isDirty || orderedIds.length === 0}
                >
                    Зберегти
                </Button>,
            ]}
        >
            <Spin spinning={loading}>
                {orderedItems.length === 0 ? (
                    <Empty description={emptyText} />
                ) : (
                    <Flex vertical gap="small" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
                        <Tag color="blue">
                            Перетягніть записи за іконку, щоб змінити порядок
                        </Tag>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                                <Flex vertical gap="small">
                                    {orderedItems.map((item, index) => (
                                        <SortableItem key={item.id} item={item} index={index} />
                                    ))}
                                </Flex>
                            </SortableContext>
                        </DndContext>
                    </Flex>
                )}
            </Spin>
        </Modal>
    );
};
